/**
 * Star Rating Service (Engine A)
 *
 * Client for the on-demand `star-engine` edge function (rate/re-rate a single
 * property) and PostgREST reads of the persisted `property_star_ratings` rows.
 *
 * `rateProperty` invokes the edge function via supabase-js (`functions.invoke`),
 * which runs auth → evidence → imagery → one Claude vision call → pure scoring →
 * persist, then returns the full rating row. `getLatestRating` reads the most
 * recent persisted rating with the raw PostgREST + `restHeaders` pattern used by
 * `market-intelligence.service.ts` (so it carries the signed-in session token,
 * not the anon key).
 *
 * Types mirror `supabase/functions/star-engine/types.ts` (`StarRating`,
 * `FieldObservations`, `Datum`, `CureItem`) plus the extra `property_star_ratings`
 * columns (migration 105) that the edge function stamps on persist. They are
 * duplicated here rather than imported because the edge function sources live
 * outside the frontend `tsconfig` `include` (`src`).
 */

import { supabase } from '@/lib/supabase';
import { supabaseUrl, restHeaders } from '@/lib/supabaseRest';

// --- observed-field envelope (mirrors star-engine/types.ts) ----------------

export type Source =
  | 'public_record'
  | 'enrichment'
  | 'visual'
  | 'om'
  | 'seller'
  | 'broker'
  | 'derived';

export interface Datum<V> {
  value: V | null;
  unit?: string | null;
  source: Source;
  confidence: number; // 0..1
  as_of?: string | null;
  verified?: boolean;
}

export type RoadSurface = 'dirt' | 'gravel_shell' | 'chip_seal' | 'asphalt' | 'concrete';
export type Condition = 'failed' | 'poor' | 'fair' | 'good' | 'excellent';
export type HomeCondition = 'dilapidated' | 'poor' | 'fair' | 'good' | 'excellent';
export type Skirting = 'none' | 'partial' | 'full';
export type CommonArea = 'neglected' | 'basic' | 'maintained' | 'manicured';
export type Signage = 'none' | 'dated' | 'attractive' | 'premium';
export type Spacing = 'crowded' | 'standard' | 'uncrowded';
export type Clutter = 'heavy' | 'moderate' | 'minimal' | 'none';
export type AmenityCond = 'none' | 'tired' | 'functional' | 'updated';

export interface FieldObservations {
  road_surface: Datum<RoadSurface>;
  road_condition: Datum<Condition>;
  road_curbed: Datum<boolean>;
  home_condition_predominant: Datum<HomeCondition>;
  home_vintage_mix: Datum<{
    pct_pre1976: number;
    pct_1976_1989: number;
    pct_1990_1999: number;
    pct_2000_2014: number;
    pct_2015plus: number;
  }>;
  home_type_mix: Datum<{ pct_single: number; pct_double: number; pct_multi: number }>;
  skirting_coverage: Datum<Skirting>;
  skirting_material: Datum<string>;
  landscaping_quality: Datum<number>; // 1..5
  common_area_appearance: Datum<CommonArea>;
  entrance_signage: Datum<Signage>;
  lot_spacing_visual: Datum<Spacing>;
  clutter_level: Datum<Clutter>;
  uniform_home_alignment: Datum<boolean>;
  amenity_clubhouse: Datum<boolean>;
  amenity_pool: Datum<boolean>;
  amenity_playground: Datum<boolean>;
  amenity_other: Datum<string[]>;
  amenity_condition_index: Datum<AmenityCond>;
  physical_occupancy_pct: Datum<number>; // 0..100, derived from records
}

export interface CureItem {
  field: keyof FieldObservations;
  description: string;
  cured_value: unknown;
  removes_gate?: string;
}

/** Per-image provenance persisted to `property_star_ratings.imagery_used` (spec §7.7). */
export interface ImageryProvenance {
  provider: string;
  kind: 'overview' | 'crop';
  gsd_cm: number;
  capture_date: string | null;
  license_verdict: string;
  bbox: [number, number, number, number];
  bbox_sr: number;
  size_px: { width: number; height: number };
}

// --- persisted rating row (property_star_ratings, migration 105) -----------

/**
 * One persisted rating: the pure `StarRating` scoring output plus the
 * point-in-time evidence/telemetry columns the edge function stamps on insert.
 */
export interface PropertyStarRating {
  id: string;
  property_id: string;

  // scoring output (StarRating)
  stars: number;
  stars_low: number;
  stars_high: number;
  open_star_flag: boolean;
  tier_confirmed: number | null;
  provisional: boolean;
  evidence_tier: 'A' | 'B' | 'C' | 'D' | null;
  location_grade: 'A' | 'B' | 'C' | 'D' | null;
  agency_eligible: boolean | null;
  implied_cap_low: number | null;
  implied_cap_high: number | null;
  cap_curve_version: string | null;
  cap_curve_asof: string | null;
  achievable_stars: number | null;
  field_observations: FieldObservations;
  gates_applied: string[];
  missing_evidence: string[];
  red_flags: string[];
  cure_items: CureItem[] | null;
  rationale: string | null;

  // point-in-time evidence + telemetry
  evidence_snapshot: Record<string, unknown> | null;
  input_hash: string;
  imagery_used: ImageryProvenance[] | null;
  rubric_version: string;
  engine_version: string | null;
  model: string | null;
  tokens_input: number | null;
  tokens_output: number | null;
  cache_read_tokens: number | null;
  cost_usd: number | null;
  is_override: boolean;
  override_by: string | null;
  override_reason: string | null;
  requested_by: string | null;
  created_at: string;
}

/**
 * Typical per-rating cost, from the first production run (2026-07-10):
 * ~23.8k input / ~3.8k output tokens on `claude-sonnet-5` ⇒ ~$0.128. Surfaced
 * for the panel's cost hint; the input-hash skip (below) is the real cost
 * control, so re-rating an unchanged property returns the cached row for $0.
 */
export const TYPICAL_RATING_COST_USD = 0.128;

// --- edge-function invoke results ------------------------------------------

/** Returned (HTTP 202) when another rate run for this property is already in flight. */
export interface RateInProgress {
  status: 'in_progress';
  property_id: string;
}

/**
 * The edge function returns the persisted rating row on success; a re-rate with
 * an unchanged `input_hash` returns the existing row flagged `cached` (no Claude
 * call); a concurrent run returns `{ status: 'in_progress' }`.
 */
export type RateResult = (PropertyStarRating & { cached?: boolean }) | RateInProgress;

/** Narrow a `rateProperty` result to the in-flight (202) case. */
export function isInProgress(result: RateResult): result is RateInProgress {
  return (result as RateInProgress).status === 'in_progress';
}

/**
 * supabase-js `FunctionsHttpError` carries the raw `Response` in `context` and
 * only a generic `message`. The star-engine error body is `{ error, details }`
 * (see `_shared/cors.ts`), so pull the server-side message when present.
 */
async function invokeErrorMessage(error: unknown, fallback: string): Promise<string> {
  const ctx = (error as { context?: unknown } | null)?.context;
  if (ctx instanceof Response) {
    try {
      const body = (await ctx.clone().json()) as { error?: unknown };
      if (typeof body?.error === 'string' && body.error) return body.error;
    } catch {
      /* body wasn't JSON — fall through */
    }
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/**
 * Rate (or re-rate) a single property. `force` skips the input-hash cache and
 * always runs a fresh Claude call. Resolves to the persisted rating row, the
 * cached row (unchanged hash), or an in-progress marker (concurrent run).
 */
export async function rateProperty(propertyId: string, force = false): Promise<RateResult> {
  const { data, error } = await supabase.functions.invoke<RateResult>('star-engine', {
    body: { property_id: propertyId, force },
  });
  if (error) throw new Error(await invokeErrorMessage(error, 'Star rating failed'));
  if (!data) throw new Error('Star rating returned no data');
  return data;
}

// --- candidate (preview) rating --------------------------------------------

/**
 * The AI-import extraction payload a web candidate is rated from (mirrors
 * `star-engine/evidence.ts` `CandidatePayload`). `extracted_data` is the
 * property's `fields_to_apply` extraction; `address` drives geocoding when
 * `latitude`/`longitude` are absent.
 */
export interface CandidateRatingPayload {
  extracted_data: Record<string, unknown>;
  address: string;
  name?: string;
  latitude?: number;
  longitude?: number;
}

/**
 * An unpersisted "preview" rating (mirrors the `handleCandidate` response in
 * `star-engine/index.ts`): the same scoring output as a persisted row minus the
 * DB identity (`candidate: true`, `persisted: false`, no `id`/`created_at`/
 * `input_hash`), plus the coordinates resolved during assembly so the caller can
 * reuse them on save. The candidate branch writes nothing server-side —
 * persisting a client-supplied rating would be a forgeable engine bypass
 * (spec §8), so web save recomputes by `property_id` via `rateProperty`.
 */
export interface CandidateRating {
  candidate: true;
  persisted: false;
  property_id: null;

  // scoring output (StarRating)
  stars: number;
  stars_low: number;
  stars_high: number;
  open_star_flag: boolean;
  tier_confirmed: number;
  provisional: boolean;
  evidence_tier: 'A' | 'B' | 'C' | 'D';
  location_grade: 'A' | 'B' | 'C' | 'D' | null;
  agency_eligible: boolean | null;
  implied_cap_low: number | null;
  implied_cap_high: number | null;
  cap_curve_version: string | null;
  cap_curve_asof: string | null;
  achievable_stars: number | null;
  star_upside: number | null;
  field_observations: FieldObservations;
  gates_applied: string[];
  missing_evidence: string[];
  red_flags: string[];
  cure_items: CureItem[] | null;
  rationale: string | null;

  // point-in-time evidence + resolved geocode + telemetry
  evidence_snapshot: Record<string, unknown> | null;
  latitude: number | null;
  longitude: number | null;
  imagery_used: ImageryProvenance[] | null;
  rubric_version: string;
  engine_version: string | null;
  model: string | null;
  tokens_input: number | null;
  tokens_output: number | null;
  cache_read_tokens: number | null;
  cost_usd: number | null;
  requested_by: string | null;
}

/**
 * Rate an AI-import extraction as a candidate (web preview). Invokes the
 * `star-engine` candidate branch, which geocodes (when coords are absent) and
 * runs the same observe → score pipeline as the by-id path, then returns the
 * rating WITHOUT persisting anything (no `property_star_ratings` row, no
 * `properties` denormalize, no `ai_change_log`). Web save recomputes + persists
 * by `property_id` via `rateProperty`.
 */
export async function rateCandidate(payload: CandidateRatingPayload): Promise<CandidateRating> {
  const { data, error } = await supabase.functions.invoke<CandidateRating>('star-engine', {
    body: { candidate: payload },
  });
  if (error) throw new Error(await invokeErrorMessage(error, 'Star rating failed'));
  if (!data) throw new Error('Star rating returned no data');
  return data;
}

/** The most recent persisted rating for a property, or null if never rated. */
export async function getLatestRating(propertyId: string): Promise<PropertyStarRating | null> {
  const res = await fetch(
    `${supabaseUrl}/rest/v1/property_star_ratings?property_id=eq.${propertyId}&select=*&order=created_at.desc&limit=1`,
    { headers: await restHeaders() }
  );
  if (!res.ok) throw new Error(`property_star_ratings -> ${res.status}`);
  const rows = (await res.json()) as PropertyStarRating[];
  return rows[0] ?? null;
}

/** Result of undoing a whole star-engine run as a unit (see `undoRatingRun`). */
export interface UndoRunResult {
  ok: boolean;
  undone: number;
  error?: string;
}

/**
 * Undo an entire star-engine run. Every change a run logs shares
 * `group_id === rating.id` — the `row_insert` for the `property_star_ratings`
 * row plus the seven denormalized `star_*` `field_update`s on `properties` — so
 * the shared `ai-command` undo endpoint reverses them together as a unit
 * (field-updates restored first, then the rating row deleted; see
 * `_shared/ai-command/undo.ts` `undoRun`). Pass the rating id as `groupId`.
 */
export async function undoRatingRun(groupId: string): Promise<UndoRunResult> {
  const { data, error } = await supabase.functions.invoke<UndoRunResult>('ai-command', {
    body: { action: 'undo', group_id: groupId },
  });
  if (error) throw new Error(await invokeErrorMessage(error, 'Undo failed'));
  if (data && data.ok === false) throw new Error(data.error ?? 'Undo failed');
  return data ?? { ok: true, undone: 0 };
}
